import React, { createContext, useCallback, useState, useMemo, useEffect, useContext } from 'react';

import { isEqualObject } from '../../../shared/utils/validation/validation';
import { MONTHLY_PERIOD_OPTION } from '../../constants/benefits';
import { PERIOD_SELECTED_MONTH } from '../../constants/metrics';
import { ISearchOptions } from '../../interfaces/benefits';
import { ICausesResponse, IMetricsContext, IPeriodSelection } from '../../interfaces/metrics';
import { getCurrentDateYYYYMMDD, getLastMonthOption } from '../../utils/datetime/datetime';
import { CompanyContext } from '../company/companyContext';

const contextDefaultValues: IMetricsContext = {
  searchOptions: {
    dateRange: ['', ''],
    period: MONTHLY_PERIOD_OPTION
  },
  changeSearchOptions: () => {
    /** */
  },
  causesData: {},
  changeCausesData: () => {
    /** */
  },
  periodSelection: {
    period: PERIOD_SELECTED_MONTH,
    month: getCurrentDateYYYYMMDD().slice(0, -2) + '01'
  },
  changePeriodSelection: () => {
    /** */
  }
};

export const MetricsContext = createContext<IMetricsContext>(contextDefaultValues);

const MetricsContextProvider = ({ children }: React.PropsWithChildren) => {
  const [causesData, setCausesData] = useState<ICausesResponse>({});
  const [searchOptions, setSearchOptions] = useState<ISearchOptions>(getLastMonthOption());
  const [periodSelection, setPeriodSelection] = useState<IPeriodSelection>(contextDefaultValues.periodSelection);
  const { company } = useContext(CompanyContext);

  const changeSearchOptions = useCallback(
    (newOptions: ISearchOptions) => {
      if (!isEqualObject(searchOptions, newOptions)) {
        setSearchOptions({ ...searchOptions, ...newOptions });
      }
    },
    [searchOptions]
  );

  const changeCausesData = useCallback((newCausesData: ICausesResponse) => {
    setCausesData(newCausesData);
  }, []);

  const changePeriodSelection = useCallback((newPeriodSelection: IPeriodSelection) => {
    setPeriodSelection(newPeriodSelection);
  }, []);

  useEffect(() => {
    setCausesData({});
  }, [company]);

  const value = useMemo(
    () => ({
      searchOptions,
      changeSearchOptions,
      causesData,
      changeCausesData,
      periodSelection,
      changePeriodSelection
    }),
    [searchOptions, changeSearchOptions, causesData, changeCausesData, periodSelection, changePeriodSelection]
  );

  return <MetricsContext.Provider value={value}>{children}</MetricsContext.Provider>;
};

export default MetricsContextProvider;
